import { Check, HelpCircle, X } from 'lucide-react';
import React, { useCallback } from 'react';

import { cn } from '@/lib/utils';
import { CheckInObjectiveResponse } from '@/services/types/treatment-types';

interface ObjectiveResponseToggleProps {
  value: CheckInObjectiveResponse;
  onChange: (value: CheckInObjectiveResponse) => void;
  disabled?: boolean;
  needsAttention?: boolean;
  className?: string;
  /** Show a 3-button toggle: Yes / No / DNA. Used for still-treating. */
  threeState?: boolean;
  /** Label for the CNC button. Defaults to "N/A" (2-state) or "DNA" (3-state). */
  cncLabel?: string;
}

export const ObjectiveResponseToggle: React.FC<
  ObjectiveResponseToggleProps
> = ({
  value,
  onChange,
  disabled = false,
  needsAttention = false,
  className,
  threeState = false,
  cncLabel,
}) => {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled) return;

      const key = e.key.toLowerCase();
      if (key === 'y') {
        e.preventDefault();
        onChange(CheckInObjectiveResponse.YES);
      } else if (key === 'n') {
        e.preventDefault();
        onChange(
          threeState
            ? CheckInObjectiveResponse.NO
            : CheckInObjectiveResponse.COULD_NOT_COLLECT,
        );
      } else if (key === 'd' || key === 'c') {
        e.preventDefault();
        onChange(CheckInObjectiveResponse.COULD_NOT_COLLECT);
      }
    },
    [disabled, onChange, threeState],
  );

  const isYes = value === CheckInObjectiveResponse.YES;
  const isNo = value === CheckInObjectiveResponse.NO;
  const isCNC = value === CheckInObjectiveResponse.COULD_NOT_COLLECT;

  const resolvedCncLabel = cncLabel ?? (threeState ? 'Unsure' : 'N/A');

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-md border border-zinc-200 bg-white',
        disabled && 'opacity-50',
        needsAttention && 'border-zinc-400',
        className,
      )}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="radiogroup"
      aria-label="Objective response"
    >
      {/* Yes button */}
      <button
        type="button"
        onClick={() => !disabled && onChange(CheckInObjectiveResponse.YES)}
        disabled={disabled}
        className={cn(
          'flex items-center gap-0.5 px-2 py-1 text-xs font-medium transition-colors',
          'rounded-l-md border-r border-zinc-200',
          'focus:outline-none focus:ring-1 focus:ring-indigo-500',
          isYes
            ? 'bg-emerald-500 text-white'
            : 'bg-white text-zinc-600 hover:bg-zinc-50',
        )}
        role="radio"
        aria-checked={isYes}
        aria-label="Yes, confirmed"
      >
        <Check className="h-3 w-3" />
        <span>Yes</span>
      </button>

      {/* No button — only in 3-state mode */}
      {threeState && (
        <button
          type="button"
          onClick={() => !disabled && onChange(CheckInObjectiveResponse.NO)}
          disabled={disabled}
          className={cn(
            'flex items-center gap-0.5 px-2 py-1 text-xs font-medium transition-colors',
            'border-r border-zinc-200',
            'focus:outline-none focus:ring-1 focus:ring-indigo-500',
            isNo
              ? 'bg-red-500 text-white'
              : 'bg-white text-zinc-600 hover:bg-zinc-50',
          )}
          role="radio"
          aria-checked={isNo}
          aria-label="No"
        >
          <X className="h-3 w-3" />
          <span>No</span>
        </button>
      )}

      {/* CNC / DNA button */}
      <button
        type="button"
        onClick={() =>
          !disabled && onChange(CheckInObjectiveResponse.COULD_NOT_COLLECT)
        }
        disabled={disabled}
        className={cn(
          'flex items-center gap-0.5 px-2 py-1 text-xs font-medium transition-colors',
          'rounded-r-md',
          'focus:outline-none focus:ring-1 focus:ring-indigo-500',
          isCNC
            ? 'bg-amber-500 text-white'
            : 'bg-white text-zinc-600 hover:bg-zinc-50',
        )}
        role="radio"
        aria-checked={isCNC}
        aria-label={threeState ? 'Unsure' : 'Could not collect'}
      >
        <HelpCircle className="h-3 w-3" />
        <span>{resolvedCncLabel}</span>
      </button>
    </div>
  );
};
